import React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import styles from './WallSizeInput.module.less';
import { translate } from '../../services/L10n';
import { t } from '../../translations';
import { metricToImperial, MM_PER_INCH } from '../../util/measures';
import { round } from '../../util/round';
import { asMm } from '../../util/measures';
import { selectUseMetric } from '../../state/dexfSettings/dexfSettingsSelectors';
import { selectDraggingWallResizer } from '../../state/scene/sceneSelectors';
import { actionSetDirtyConfiguration } from '../../state/vpc/vpcActions';

const INTEGER_REGEX = /(^\d+$)|(^$)/;

class WallSizeInput extends React.Component {
  static propTypes = {
    min: PropTypes.number,
    max: PropTypes.number,
    val: PropTypes.number.isRequired,
    top: PropTypes.number.isRequired,
    left: PropTypes.number.isRequired,
    onComplete: PropTypes.func.isRequired,
    setDirtyConfig: PropTypes.func.isRequired,
    isHorizontal: PropTypes.bool,
    kiosk: PropTypes.bool,
    draggingWallResizer: PropTypes.bool,
    poppedOut: PropTypes.bool.isRequired,
    useMetricMeasures: PropTypes.bool.isRequired,
  };

  constructor(props) {
    super(props);
    this.onBlur = this.onBlur.bind(this);
    this.onComplete = this.onComplete.bind(this);
    this.onChange = this.onChange.bind(this);
    this.onFocus = this.onFocus.bind(this);
    this.onKeyUp = this.onKeyUp.bind(this);
    this.focusTimer = null;
    this.state = {
      values: WallSizeInput.getValues(props.val, true, props.useMetricMeasures),
      isFocused: false,
    };
  }

  componentWillUnmount() {
    clearTimeout(this.focusTimer);
  }

  static getDerivedStateFromProps(props, state) {
    if (!state.isFocused && asMm(state.values) !== props.val) {
      return {
        values: WallSizeInput.getValues(
          props.val,
          true,
          props.useMetricMeasures
        ),
      };
    }
    return null;
  }

  static getValues(val, isMM, useMetricMeasures) {
    const valueInMm = isMM ? val : asMm(val);
    if (useMetricMeasures) {
      return {
        imp: false,
        minor: !isMM && val.minor === '' ? '' : Math.round(valueInMm / 10),
      };
    }
    const imp = metricToImperial(round(valueInMm, MM_PER_INCH));

    return {
      imp: true,
      major: !isMM && val.major === '' ? '' : Number(imp.feet),
      minor:
        !isMM && val.minor === '' ? '' : Number(imp.inches.replace(/\D/g, '')),
    };
  }

  onChange(source, event) {
    this.props.setDirtyConfig();
    if (!INTEGER_REGEX.test(event.target.value)) {
      return;
    }
    const newValues = { ...this.state.values };
    newValues[source] = event.target.value;

    this.setState({
      values: WallSizeInput.getValues(
        newValues,
        false,
        this.props.useMetricMeasures
      ),
    });
  }

  onFocus(event) {
    event.target.select();
    clearTimeout(this.focusTimer);
    this.setState({ isFocused: true });
  }

  onKeyUp(event) {
    if (event.key === 'Enter') {
      event.target.blur();
    }
  }

  render() {
    const values = this.state.values;
    return (
      <div
        className={classNames(
          styles.wrapper,
          styles.common,
          this.props.isHorizontal ? styles.horizontal : styles.vertical,
          !this.props.kiosk && this.state.isFocused && styles.focus,
          this.props.draggingWallResizer && styles.disableAnimation,
          this.props.poppedOut && styles.poppedOut
        )}
        onBlur={this.onBlur}
        style={{ top: this.props.top, left: this.props.left }}
      >
        {values.imp && (
          <>
            <input
              type="text"
              pattern="\d*"
              onKeyUp={this.onKeyUp}
              className={classNames(styles.input, styles.common, styles.major)}
              step={1}
              value={Number.isFinite(values.major) ? Number(values.major) : ''}
              onChange={this.onChange.bind(null, 'major')}
              onFocus={this.onFocus}
              readOnly={this.props.kiosk}
              disabled={this.props.kiosk}
            />
            <span>{translate(t.MEASURE_VALUE_FEET)}</span>
          </>
        )}

        <input
          type="text"
          pattern="\d*"
          onKeyUp={this.onKeyUp}
          className={classNames(styles.input, styles.common, styles.minor)}
          step={1}
          value={Number.isFinite(values.minor) ? Number(values.minor) : ''}
          onChange={this.onChange.bind(null, 'minor')}
          onFocus={this.onFocus}
          readOnly={this.props.kiosk}
          disabled={this.props.kiosk}
        />
        <span>
          {this.props.useMetricMeasures
            ? translate(t.MEASURE_VALUE_CM)
            : translate(t.MEASURE_VALUE_IN)}
        </span>
      </div>
    );
  }

  onBlur() {
    this.onComplete();
  }

  onComplete() {
    this.focusTimer = setTimeout(() => {
      const clamped = Math.min(
        Math.max(asMm(this.state.values), this.props.min || -Infinity),
        this.props.max || Infinity
      );

      this.props.onComplete(clamped);

      this.setState({
        values: WallSizeInput.getValues(
          clamped,
          true,
          this.props.useMetricMeasures
        ),
        isFocused: false,
      });
    }, 50);
  }
}

export default connect(
  state => ({
    draggingWallResizer: selectDraggingWallResizer(state),
    useMetricMeasures: selectUseMetric(state),
  }),
  dispatch => ({
    setDirtyConfig: () => dispatch(actionSetDirtyConfiguration(true)),
  })
)(WallSizeInput);
